import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { customInstance, AXIOS_INSTANCE } from '../../services/api-client'
import type {
  VariableLagAccrualRun,
  VariableLagConfig,
  VariableLagConfigUpdate,
  VariableLagConnectorOption,
  VariableLagHealth,
} from './types'

const BASE = '/api/variable-lag-accruals'

export const variableLagKeys = {
  all: ['variable-lag-accruals'] as const,
  run: () => [...variableLagKeys.all, 'run'] as const,
  health: () => [...variableLagKeys.all, 'health'] as const,
  config: () => [...variableLagKeys.all, 'config'] as const,
  connectors: () => [...variableLagKeys.all, 'connectors'] as const,
}

export function useVariableLagRun(options?: { enabled?: boolean }) {
  return useQuery<VariableLagAccrualRun>({
    queryKey: variableLagKeys.run(),
    queryFn: () => customInstance({ url: `${BASE}/run`, method: 'GET' }),
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
  })
}

export function useVariableLagHealth() {
  return useQuery<VariableLagHealth>({
    queryKey: variableLagKeys.health(),
    queryFn: () => customInstance({ url: `${BASE}/health`, method: 'GET' }),
    staleTime: 60_000,
    retry: 1,
  })
}

export function useVariableLagDataSourceConfig() {
  return useQuery<VariableLagConfig>({
    queryKey: variableLagKeys.config(),
    queryFn: () => customInstance({ url: `${BASE}/config/data-source`, method: 'GET' }),
    staleTime: 60_000,
  })
}

export function useVariableLagAvailableConnectors() {
  return useQuery<VariableLagConnectorOption[]>({
    queryKey: variableLagKeys.connectors(),
    queryFn: () => customInstance({ url: `${BASE}/config/available-connectors`, method: 'GET' }),
    staleTime: 60_000,
  })
}

export function useSetVariableLagDataSource() {
  const queryClient = useQueryClient()
  return useMutation<VariableLagConfig, Error, VariableLagConfigUpdate>({
    mutationFn: payload =>
      customInstance({
        url: `${BASE}/config/data-source`,
        method: 'PUT',
        data: payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: variableLagKeys.all })
    },
  })
}

export function useClearVariableLagDataSource() {
  const queryClient = useQueryClient()
  return useMutation<VariableLagConfig, Error, void>({
    mutationFn: () =>
      customInstance({
        url: `${BASE}/config/data-source`,
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: variableLagKeys.all })
    },
  })
}

export function useTestVariableLagConnection() {
  return useMutation<VariableLagHealth, Error, VariableLagConfigUpdate>({
    mutationFn: payload =>
      customInstance({
        url: `${BASE}/config/test-connection`,
        method: 'POST',
        data: payload,
      }),
  })
}

export async function downloadVariableLagExport(exportType: 'je' | 'reversal') {
  const response = await AXIOS_INSTANCE.get(`${BASE}/exports/${exportType}`, {
    responseType: 'blob',
  })
  const disposition = response.headers['content-disposition'] || ''
  const match = disposition.match(/filename="([^"]+)"/)
  const filename =
    match?.[1] || (exportType === 'je'
      ? 'EC02d_JE_Load_Apr2025.csv'
      : 'EC02d_Reversal_Load_May2025.csv')
  const url = window.URL.createObjectURL(response.data)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export async function openVariableLagReport() {
  const response = await AXIOS_INSTANCE.get(`${BASE}/report-html`, {
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(response.data)
  window.open(url, '_blank', 'noopener,noreferrer')
  window.setTimeout(() => window.URL.revokeObjectURL(url), 30_000)
}
